import { ICellRendererAngularComp } from 'ag-grid-angular';
import { IRowNode } from 'ag-grid-community';

import { Component } from '@angular/core';
import { ProgressBarStatus } from '@core/enums/progress-bar-status.enum';
import { PlanningBoardSettings } from '@core/models/planning-board-settings';
import { PeriodRendererParams } from '@core/typings/period-renderer.params';
import { NodeUtils } from '@core/utils/node.utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Period } from '@portal-shared/app/entities/period.model';
import { PeriodUsageDto } from '@services/dtos/rows/period-usage.dto';
import { CellDropZoneComponent } from '@shared/components/cell-drop-zone/cell-drop-zone.component';
import { PeriodSumsComponent } from '@shared/components/grids/row-cell-renderers/period-sums/period-sums.component';
import { ProgressBarComponent } from '@shared/components/progress-bar/progress-bar.component';
import { PlanningGroupRow } from '../../rows/groups/planning-group.row';
import { ResourceGroupRow } from '../../rows/groups/resource-group.row';

@Component({
    selector: 'al-adequacy-period-cell-renderer',
    templateUrl: './adequacy-period-cell-renderer.component.html',
    styleUrls: ['./adequacy-period-cell-renderer.component.scss'],
    imports: [ProgressBarComponent, FontAwesomeModule, CellDropZoneComponent, PeriodSumsComponent]
})
export class AdequacyPeriodCellRendererComponent implements ICellRendererAngularComp {
  public PeriodAdequacyStatus = ProgressBarStatus;

  public adequacy = 0;
  public status = ProgressBarStatus.Warning;

  public plannedHours = 0;
  public actualHours?: number;

  public node?: IRowNode<PlanningGroupRow>;
  public period?: Period;

  public hasNoPlanification = false;
  public isLoading = true;

  public get isDroppable() {
    return NodeUtils.isCellDroppable(this.node);
  }

  public isResourceRow = false;

  public agInit(params: PeriodRendererParams<PlanningGroupRow, PeriodUsageDto>) {
    this.update(params);
  }

  public refresh(params: PeriodRendererParams<PlanningGroupRow, PeriodUsageDto>): boolean {
    this.update(params);
    return true;
  }

  private update(params: PeriodRendererParams<PlanningGroupRow, PeriodUsageDto>) {
    this.adequacy = 0;
    this.node = params.node;
    this.period = params.period;
    this.isResourceRow = params.data instanceof ResourceGroupRow;

    const planningSettings = params.planningBoardSettings;

    if (!params.value) return;

    this.isLoading = false;
    this.computeProjectOrDepartmentAdequacy(params.value);
    this.status = this.computeStatus(planningSettings, this.adequacy);
  }

  public expand(expand = true) {
    this.node?.setExpanded(expand);
  }
  public toggleExpand() {
    this.expand(!this.node?.expanded);
  }

  public computeProjectOrDepartmentAdequacy({ plannedHours, realHours: actualHours, adequacy }: PeriodUsageDto) {
    this.hasNoPlanification = !plannedHours;
    this.plannedHours = plannedHours ?? 0;
    this.actualHours = actualHours ?? 0;
    this.adequacy = adequacy ?? 0;
  }

  public computeStatus(planningSettings: PlanningBoardSettings, adequacy: number) {
    if (this.hasNoPlanification) {
      return ProgressBarStatus.Neutral;
    }

    const percentage = adequacy * 100;
    let status = ProgressBarStatus.Neutral;

    if (planningSettings) {
      const { lowAdequacyThreshold, mediumAdequacyThreshold, highAdequacyThreshold } = planningSettings;

      if (percentage >= highAdequacyThreshold) {
        status = ProgressBarStatus.Good;
      } else if (percentage >= mediumAdequacyThreshold) {
        status = ProgressBarStatus.Warning;
      } else if (percentage >= lowAdequacyThreshold) {
        status = ProgressBarStatus.Error;
      }
    }

    return status;
  }
}
